import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { useCart } from '../context/CartContext';
import { useSelectSucursal } from '../hooks/useSelectSucursal';
import {
  cityKey,
  filterSucursales,
  sameCity,
  sameZone,
  uniqueCities,
  uniqueZonas,
  zoneKey,
} from '../utils/sucursalFilters';
import { fixClienteDisplay, fixMojibakeText } from '../utils/textEncoding';

const SUCURSALES_CACHE_KEY = 'oh_sucursales_cache_v3';

function readSucursalesCache() {
  try {
    const data = JSON.parse(localStorage.getItem(SUCURSALES_CACHE_KEY) || '[]');
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

function writeSucursalesCache(rows) {
  try {
    localStorage.setItem(SUCURSALES_CACHE_KEY, JSON.stringify(rows.slice(0, 500)));
  } catch {
    /* ignore */
  }
}

function clearSucursalesCache() {
  try {
    localStorage.removeItem(SUCURSALES_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

export default function SucursalPicker() {
  const { state } = useCart();
  const { selectSucursal, selecting } = useSelectSucursal();
  const [q, setQ] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [zona, setZona] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const [facetCities, setFacetCities] = useState([]);
  const [facetZonas, setFacetZonas] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [panelStyle, setPanelStyle] = useState(null);
  const rootRef = useRef(null);
  const btnRef = useRef(null);

  // Si Admin crea/importa clientes, invalidamos y refrescamos con el panel abierto.
  useEffect(() => {
    function onInvalidate(e) {
      const domains = e.detail?.domains || [];
      if (!domains.includes('client') && domains.length) return;
      clearSucursalesCache();
      if (open) setReloadTick((n) => n + 1);
    }
    window.addEventListener('oh:invalidate', onInvalidate);
    return () => window.removeEventListener('oh:invalidate', onInvalidate);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .getSucursales({
          q: q.trim() || undefined,
          ciudad: ciudad || undefined,
          zona: zona || undefined,
          limit: 200,
          facets: 1,
        })
        .then((r) => {
          const rows = (r.data || [])
            .map(fixClienteDisplay)
            .filter((s) => !(s.es_sede || s.padre_id));
          setSucursales(rows);
          setFromCache(false);
          if (!ciudad && Array.isArray(r.facets?.ciudades) && r.facets.ciudades.length) {
            setFacetCities(r.facets.ciudades.map(fixMojibakeText).filter(Boolean));
          }
          if (Array.isArray(r.facets?.zonas)) {
            setFacetZonas(r.facets.zonas.map(fixMojibakeText).filter(Boolean));
          } else if (!ciudad) {
            setFacetZonas([]);
          }
          if (!q.trim() && !ciudad && !zona && rows.length) writeSucursalesCache(rows);
          else if (rows.length) {
            const prev = readSucursalesCache();
            const map = new Map(prev.map((s) => [s.id, s]));
            rows.forEach((s) => map.set(s.id, s));
            writeSucursalesCache([...map.values()]);
          }
        })
        .catch(() => {
          const cached = filterSucursales(readSucursalesCache(), { q, ciudad, zona }).filter(
            (s) => !(s.es_sede || s.padre_id)
          );
          setSucursales(cached);
          setFacetCities(uniqueCities(readSucursalesCache().filter((s) => !(s.es_sede || s.padre_id))));
          setFacetZonas(
            ciudad
              ? uniqueZonas(
                  readSucursalesCache().filter((s) => !(s.es_sede || s.padre_id)),
                  ciudad
                )
              : []
          );
          setFromCache(true);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, ciudad, zona, open, reloadTick]);

  useEffect(() => {
    if (!open) return undefined;

    function positionPanel() {
      const btn = btnRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 320;
      setPanelStyle({
        position: 'fixed',
        left: Math.max(8, rect.left),
        width: Math.min(Math.max(rect.width, 280), window.innerWidth - 16),
        top: openUp ? undefined : rect.bottom + 8,
        bottom: openUp ? window.innerHeight - rect.top + 8 : undefined,
        zIndex: 200,
      });
    }

    positionPanel();
    window.addEventListener('resize', positionPanel);
    window.addEventListener('scroll', positionPanel, true);

    function onPointerDown(e) {
      if (rootRef.current?.contains(e.target)) return;
      const panel = document.getElementById('sucursal-picker-panel');
      if (panel?.contains(e.target)) return;
      setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return () => {
      window.removeEventListener('resize', positionPanel);
      window.removeEventListener('scroll', positionPanel, true);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const ciudades = useMemo(() => {
    if (facetCities.length) return facetCities;
    const cached = readSucursalesCache();
    const base = sucursales.length ? sucursales : cached;
    return uniqueCities(base.length ? base : cached);
  }, [facetCities, sucursales]);

  const zonas = useMemo(() => {
    if (!ciudad) return [];
    if (facetZonas.length) return facetZonas;
    return uniqueZonas(sucursales.length ? sucursales : readSucursalesCache(), ciudad);
  }, [ciudad, facetZonas, sucursales]);

  async function seleccionar(s) {
    if (selecting) return;
    await selectSucursal(s);
    setOpen(false);
    setQ('');
    setCiudad('');
    setZona('');
  }

  const label = state.cliente.sucursal_codigo
    ? state.cliente.es_sede || state.cliente.padre_codigo
      ? `Sucursal · ${state.cliente.padre_codigo || state.cliente.sucursal_codigo} · ${
          state.cliente.sede_etiqueta || state.cliente.nombre
        }`
      : `${state.cliente.sucursal_codigo} · ${state.cliente.nombre}`
    : 'Seleccionar cliente / sucursal';

  const panel = open && panelStyle ? (
    <div id="sucursal-picker-panel" className="cliente-picker-panel cliente-picker-panel-fixed" style={panelStyle}>
      <input
        type="search"
        placeholder="Buscar código, nombre, ciudad, barrio..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
      />
      {ciudades.length ? (
        <div className="mis-clientes-city-chips picker-city-chips" role="listbox" aria-label="Filtrar por ciudad">
          <button
            type="button"
            className={`mis-clientes-city-chip ${!ciudad ? 'active' : ''}`}
            onClick={() => {
              setCiudad('');
              setZona('');
            }}
          >
            Todas
          </button>
          {ciudades.map((c) => (
            <button
              key={cityKey(c)}
              type="button"
              className={`mis-clientes-city-chip ${sameCity(ciudad, c) ? 'active' : ''}`}
              onClick={() => {
                setCiudad((prev) => (sameCity(prev, c) ? '' : c));
                setZona('');
              }}
            >
              {c}
            </button>
          ))}
        </div>
      ) : null}
      {ciudad && zonas.length ? (
        <div className="mis-clientes-city-chips picker-city-chips mis-clientes-zona-chips" role="listbox" aria-label="Filtrar por barrio">
          <button
            type="button"
            className={`mis-clientes-city-chip ${!zona ? 'active' : ''}`}
            onClick={() => setZona('')}
          >
            Todos los barrios
          </button>
          {zonas.map((z) => (
            <button
              key={zoneKey(z)}
              type="button"
              className={`mis-clientes-city-chip ${sameZone(zona, z) ? 'active' : ''}`}
              onClick={() => setZona((prev) => (sameZone(prev, z) ? '' : z))}
            >
              {z}
            </button>
          ))}
        </div>
      ) : null}
      {fromCache ? (
        <p className="picker-offline-hint">Sin internet — clientes guardados en la tablet</p>
      ) : null}
      <div className="cliente-picker-list">
        {loading ? <p className="picker-loading">Buscando...</p> : null}
        {!loading && !sucursales.length ? (
          <p className="picker-empty">
            {fromCache ? 'No hay clientes guardados para esta búsqueda' : 'Sin resultados'}
          </p>
        ) : null}
        {sucursales.map((s) => (
          <button key={`${s.id}-${s.codigo}`} type="button" disabled={selecting} onClick={() => seleccionar(s)}>
            <strong>
              Cód. {s.codigo} · {s.display_nombre || s.nombre}
            </strong>
            <span>
              {s.ciudad}
              {s.zona ? ` · ${s.zona}` : ''}
            </span>
            <span>{s.direccion}</span>
            {s.vendedor_nombre ? <em>Vendedor: {s.vendedor_nombre}</em> : null}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className={`cliente-picker ${open ? 'open' : ''}`} ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className="cliente-picker-btn"
        disabled={selecting}
        onClick={() => setOpen((v) => !v)}
      >
        {selecting ? 'Cargando cliente…' : label}
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </div>
  );
}
